import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { getDateInputValue, validateIndianMobile } from '../utils/helpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOLDINGS = [
  ['mutual_funds', 'Mutual Funds'], ['loans', 'Loans'], ['fd', 'Fixed Deposit'],
  ['rd', 'Recurring Deposit'], ['post_office', 'Post Office Schemes'], ['ppf', 'PPF'],
];
const DEFAULT_HOLDINGS = { mutual_funds: 'unknown', loans: 'unknown', fd: 'unknown', rd: 'unknown', post_office: 'unknown', ppf: 'unknown' };

const toForm = (c) => ({
  client_type: c.client_type || 'Individual', name: c.name || '',
  dob: getDateInputValue(c.dob), date_of_marriage: getDateInputValue(c.date_of_marriage),
  gender: c.gender || 'Male', primary_mobile: c.primary_mobile || '', secondary_mobile: c.secondary_mobile || '',
  email: c.email || '', pan: c.pan || '', aadhaar: c.aadhaar || '', address: c.address || '',
  nominee: c.nominee || '', financial_holdings: { ...DEFAULT_HOLDINGS, ...(c.financial_holdings || {}) },
});

const HoldingsPicker = ({ value, onChange }) => (
  <details className="rounded-lg border border-slate-200 bg-slate-50/60 open:bg-white" data-testid="edit-client-holdings-section">
    <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-800 select-none">Other Financial Holdings</summary>
    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
      {HOLDINGS.map(([key, label]) => (
        <div key={key} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-slate-200 bg-white">
          <span className="text-sm text-slate-800">{label}</span>
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5">
            {['yes', 'no', 'unknown'].map((v) => {
              const active = (value?.[key] || 'unknown') === v;
              const cls = active ? (v === 'yes' ? 'bg-emerald-600 text-white' : v === 'no' ? 'bg-orange-600 text-white' : 'bg-slate-500 text-white') : 'text-slate-600 hover:bg-slate-100';
              return (
                <button key={v} type="button" onClick={() => onChange({ ...value, [key]: v })}
                  className={`px-2 py-0.5 text-[11px] rounded font-medium capitalize ${cls}`} data-testid={`edit-holding-${key}-${v}`}>
                  {v === 'unknown' ? '?' : v}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  </details>
);

const DuplicateAlert = ({ dupes, onCancel, onConfirm, saving }) => (
  <AlertDialog open={!!dupes} onOpenChange={(o) => { if (!o) onCancel(); }}>
    <AlertDialogContent data-testid="edit-client-duplicate-alert">
      <AlertDialogHeader>
        <AlertDialogTitle>Possible duplicate client</AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div className="space-y-2 text-sm text-slate-600">
            <p>Another of your clients already uses this mobile or email:</p>
            <ul className="space-y-1" data-testid="edit-client-duplicate-list">
              {(dupes || []).map(d => (
                <li key={d.client_id} className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-amber-900">
                  <span className="font-semibold">{d.name}</span>{d.customer_code ? ` (${d.customer_code})` : ''} — same {d.fields.join(' & ')}
                </li>
              ))}
            </ul>
            <p>Save anyway, or cancel to correct the details.</p>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="edit-client-duplicate-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={saving}
          className="bg-amber-600 hover:bg-amber-700" data-testid="edit-client-duplicate-save-anyway">
          {saving ? 'Saving…' : 'Save anyway'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const EditClientDialog = ({ open, onOpenChange, client, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState(null);

  useEffect(() => { if (open && client) { setForm(toForm(client)); setDupes(null); } }, [open, client]);

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const save = async (allowDuplicate = false) => {
    const mv = validateIndianMobile(form.primary_mobile);
    if (!mv.ok) { toast.error(mv.error); return; }
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim(), primary_mobile: mv.digits, email: form.email.trim() };
      await axios.put(`${API}/clients/${client.client_id}${allowDuplicate ? '?allow_duplicate=true' : ''}`, payload, { withCredentials: true });
      toast.success('Client updated');
      setDupes(null);
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.duplicates) setDupes(detail.duplicates);
      else toast.error((typeof detail === 'string' && detail) || 'Failed to update client');
    } finally { setSaving(false); }
  };

  const field = (label, key, props = {}) => (
    <div className={`space-y-2 ${props.wide ? 'col-span-2' : ''}`}>
      <Label>{label}</Label>
      <Input value={form[key]} onChange={(e) => set({ [key]: props.transform ? props.transform(e.target.value) : e.target.value })}
        type={props.type || 'text'} maxLength={props.maxLength} inputMode={props.inputMode} data-testid={`edit-client-${key.replace(/_/g, '-')}-input`} />
      {props.hint && <p className="text-[10px] text-slate-500">{props.hint}</p>}
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
            <DialogDescription>Update any detail below. Policies, family links and documents stay as they are.</DialogDescription>
          </DialogHeader>
          {form && (
            <form onSubmit={(e) => { e.preventDefault(); save(false); }} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Client Type</Label>
                  <Select value={form.client_type} onValueChange={(v) => set({ client_type: v })}>
                    <SelectTrigger data-testid="edit-client-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Individual">Individual</SelectItem><SelectItem value="Corporate">Corporate</SelectItem></SelectContent>
                  </Select>
                </div>
                {field('Name *', 'name', { wide: true })}
                {field('Date of Birth', 'dob', { type: 'date' })}
                {field('Date of Marriage', 'date_of_marriage', { type: 'date' })}
                <div className="space-y-2"><Label>Gender</Label>
                  <Select value={form.gender} onValueChange={(v) => set({ gender: v })}>
                    <SelectTrigger data-testid="edit-client-gender-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                  </Select>
                </div>
                {field('Primary Mobile *', 'primary_mobile', { inputMode: 'numeric', maxLength: 10, hint: 'Exactly 10 digits, starts with 6-9', transform: v => v.replace(/\D/g, '').slice(0, 10) })}
                {field('Secondary Mobile', 'secondary_mobile')}
                {field('Email', 'email', { type: 'email' })}
                {field('PAN', 'pan', { maxLength: 10, transform: v => v.toUpperCase() })}
                {field('Aadhaar', 'aadhaar', { maxLength: 12 })}
                {field('Address', 'address', { wide: true })}
                {field('Nominee', 'nominee', { wide: true })}
              </div>
              <HoldingsPicker value={form.financial_holdings} onChange={(v) => set({ financial_holdings: v })} />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="edit-client-cancel">Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-blue-800 hover:bg-blue-900" data-testid="edit-client-save">
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
      <DuplicateAlert dupes={dupes} saving={saving} onCancel={() => setDupes(null)} onConfirm={() => save(true)} />
    </>
  );
};

export default EditClientDialog;
